import { useSelector, useStore, useDispatch } from '@bridebook/web/lib/utils';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import gazetteer from '@bridebook/toolbox/src/gazetteer';
import { useCallback } from 'react';
import enquirySupplierTrack from '@bridebook/web/lib/utils/server-side-track/enquiry-supplier-track';
import { CriticalWebEvents } from '@bridebook/web/lib/analyticsTypes';
import { weddingDetailsPropertiesGeneric } from '@bridebook/web/lib/weddings/analytics';
import { getConversationId } from '@bridebook/toolbox/src/inbox/api/talkjs/get-conversation-id';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { shortlistedSupplierAnalytics } from '@bridebook/web/lib/shortlist/actions';
import {
  getShowroundAnalyticsContext,
  getShowroundSupplierContext,
  getShowroundSupplierContextId,
} from '../state';
import { useShowroundCalendar } from '@bridebook/web/lib/showrounds/hooks/use-showround-calendar';

export const useShowroundBookingAnalytics = (isOpenDay?: boolean) => {
  const supplierId = useSelector(getShowroundSupplierContextId);
  const weddingId = useSelector(getWeddingId);
  const supplier = useSelector(getShowroundSupplierContext);
  const analyticsContext = useSelector(getShowroundAnalyticsContext);
  const supplierCountryCode = supplier?.l10n.country;
  const supplierCountryName = supplierCountryCode
    ? gazetteer.getMarketByCountry(supplierCountryCode).getCountryName()
    : '';
  const store = useStore();
  const dispatch = useDispatch();
  const conversationId = supplierId ? getConversationId([supplierId, weddingId]) : undefined;
  const { slots } = useShowroundCalendar(supplierId as string, {
    enabled: !!supplierId,
  });
  const shortlistedMethod = isOpenDay
    ? 'onEnquirySend_OpenDaysModal'
    : 'onEnquirySend_showroundsCalendarModal';

  const trackEmailedSupplier = useCallback(
    (eventId?: string) => {
      const state = store.getState();

      return enquirySupplierTrack({
        event: CriticalWebEvents.EMAILED_SUPPLIER,
        state,
        contactedSupplier: supplier as unknown as IUISupplier,
        category: 'Supplier Engagement',
        specificEventProps: {
          weddingId,
          contactLocation: analyticsContext?.actionLocation,
          contactType: 'singleEnquiry',
          contactMessage: '',
          contactSection: analyticsContext?.actionSection,
          contactMethod: 'showroundsCalendarModal',
          cta: 'Book a viewing',
          ...weddingDetailsPropertiesGeneric(state),
          supplierCategory: supplier?.type,
          supplierCountryCode,
          supplierCountryName,
          conversationId,
          eventId,
          numberOfActiveShowroundSlots: slots.length,
        },
      });
    },
    [
      store,
      supplier,
      weddingId,
      analyticsContext?.actionLocation,
      analyticsContext?.actionSection,
      supplierCountryCode,
      supplierCountryName,
      conversationId,
      slots.length,
    ],
  );

  const trackFailedToSendEnquiry = useCallback(() => {
    const state = store.getState();
    return enquirySupplierTrack({
      event: 'Failed to send enquiry',
      state,
      contactedSupplier: supplier as unknown as IUISupplier,
      category: 'Supplier Engagement',
      specificEventProps: {
        weddingId,
        contactLocation: analyticsContext?.actionLocation,
        contactType: 'singleEnquiry',
        contactSection: analyticsContext?.actionSection,
        contactMethod: 'showroundsCalendarModal',
        cta: 'Book a viewing',
        ...weddingDetailsPropertiesGeneric(state),
        supplierCategory: supplier?.type,
        supplierCountryCode,
        supplierCountryName,
        conversationId,
      },
    });
  }, [
    store,
    supplier,
    analyticsContext,
    supplierCountryCode,
    supplierCountryName,
    conversationId,
    weddingId,
  ]);

  const trackShortlistedSupplier = useCallback(
    () =>
      dispatch(
        shortlistedSupplierAnalytics({
          supplier: supplier as unknown as IUISupplier,
          method: shortlistedMethod,
          shortlistedLocation: 'supplierProfile',
          shortlistedSection: analyticsContext?.actionSection ?? 'aboveTheFold',
          linkedSupplierName: supplier?.name,
          linkedSupplierId: supplierId,
        }),
      ),
    [dispatch, supplier, shortlistedMethod, analyticsContext?.actionSection, supplierId],
  );

  return {
    trackEmailedSupplier,
    trackFailedToSendEnquiry,
    trackShortlistedSupplier,
  };
};
